import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { useAuthStore } from "@/store/authStore";
import { StorefrontEditor } from "@/components/storefront/StorefrontEditor";
import { StorefrontPreview } from "@/components/storefront/StorefrontPreview";
import { StorefrontControls } from "@/components/storefront/StorefrontControls";
import { Button } from "@/components/ui/button";
import { Eye, Edit3, Save } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Testimonial {
  id: string;
  name: string;
  review: string;
  rating: number;
  role?: string;
}

interface StorefrontContent {
  hero: {
    title: string;
    subtitle: string;
    buttonText: string;
    buttonAction: string;
    backgroundImage?: string;
    images: string[];
  };
  colors: {
    background: string;
    primary: string;
    secondary: string;
    accent: string;
    text: string;
  };
  testimonials?: Testimonial[];
}

const defaultContent: StorefrontContent = {
  hero: {
    title: "Welcome to Our Store",
    subtitle: "Discover amazing products and services",
    buttonText: "Explore Now",
    buttonAction: "#menu",
    backgroundImage: "",
    images: [],
  },
  colors: {
    background: "#ffffff",
    primary: "#6366F1",
    secondary: "#EC4899",
    accent: "#8B5CF6",
    text: "#2C3E50",
  },
  testimonials: [
    {
      id: "1",
      name: "Sarah Johnson",
      review:
        "Amazing food and incredible service! The atmosphere is perfect for a romantic dinner or family gathering.",
      rating: 5,
      role: "Food Blogger",
    },
    {
      id: "2",
      name: "Mike Chen",
      review:
        "The best dining experience I've had in years. Every dish was perfectly prepared and beautifully presented.",
      rating: 5,
      role: "Regular Customer",
    },
    {
      id: "3",
      name: "Emily Davis",
      review:
        "Outstanding quality and attention to detail. The staff goes above and beyond to make every visit special.",
      rating: 5,
      role: "Local Resident",
    },
  ],
};

export default function StoreFront() {
  const { storeId } = useParams();
  const { stores, selectedStoreId } = useStoreManagementStore();
  const { isAuthenticated } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState<StorefrontContent>(defaultContent);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [publishedSlug, setPublishedSlug] = useState("");
  const [publishSlug, setPublishSlug] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);

  const currentStore = stores.find(
    (store) => store.id === (storeId || selectedStoreId)
  );

  // Load storefront content from database when component mounts
  useEffect(() => {
    const loadStorefrontContent = async () => {
      if (!currentStore?.id) return;

      try {
        const { data: storeData, error } = await supabase
          .from("stores")
          .select("storefront_content, slug")
          .eq("id", currentStore.id)
          .single();

        if (error) {
          console.error("Error loading storefront content:", error);
          return;
        }

        if (storeData?.storefront_content) {
          setContent(JSON.parse(storeData.storefront_content as string));
        }

        if (storeData?.slug) {
          setIsPublished(true);
          setPublishedSlug(storeData.slug);
        }
      } catch (error) {
        console.error("Failed to load storefront content:", error);
      }
    };

    loadStorefrontContent();
  }, [currentStore?.id]);

  // Check if store is already published
  useEffect(() => {
    if (currentStore?.slug) {
      setIsPublished(true);
      setPublishedSlug(currentStore.slug);
    }
  }, [currentStore]);

  const handleContentChange = (updatedContent: Partial<StorefrontContent>) => {
    setContent((prev) => {
      const newContent = { ...prev, ...updatedContent };
      setHasUnsavedChanges(true);
      return newContent;
    });
  };

  const handleSave = async () => {
    if (!currentStore?.id) return;

    try {
      const { error } = await supabase
        .from("stores")
        .update({ storefront_content: JSON.stringify(content) })
        .eq("id", currentStore.id);

      if (error) {
        console.error("Error saving storefront content:", error);
        throw error;
      }

      toast({
        title: "Changes saved",
        description: "Your storefront has been updated successfully.",
      });
      setHasUnsavedChanges(false);
    } catch (error) {
      toast({
        title: "Error saving changes",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePublish = async () => {
    if (!currentStore || !publishSlug.trim()) return;

    setIsPublishing(true);

    try {
      // Generate unique slug using the database function
      const { data: uniqueSlug, error: slugError } = await supabase.rpc(
        "generate_store_slug_from_name",
        { store_name: currentStore.name }
      );

      if (slugError) {
        console.error("Error generating slug:", slugError);
        throw slugError;
      }

      const finalSlug = publishSlug || uniqueSlug;

      // Update the store with the slug, storefront content, and other data
      const { error: updateError } = await supabase
        .from("stores")
        .update({
          slug: finalSlug,
          storefront_content: JSON.stringify(content),
          business_hours: currentStore.business_hours,
        })
        .eq("id", currentStore.id);

      if (updateError) {
        console.error("Error updating store:", updateError);
        throw updateError;
      }

      setIsPublished(true);
      setPublishedSlug(finalSlug);
      setIsEditing(false);
      setHasUnsavedChanges(false);

      toast({
        title: "Storefront published!",
        description: `Your storefront is now live at /store/${finalSlug}`,
      });

      // Update the store in the local state
      const updatedStore = { ...currentStore, slug: finalSlug };
      // You might want to update the store management store here
    } catch (error) {
      console.error("Failed to publish storefront:", error);
      toast({
        title: "Failed to publish",
        description: "Please try again.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsPublishing(false);
    }
  };

  const generateSlug = () => {
    if (!currentStore) return;
    const baseSlug = currentStore.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setPublishSlug(baseSlug);
  };

  if (!currentStore) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Store not found....
          </h1>
          <p className="text-gray-600">
            The requested store could not be found.
          </p>
        </div>
      </div>
    );
  }

  // Determine if user can edit - only authenticated users can edit
  const canEdit = isAuthenticated;
  console.log("canEdit.........", canEdit);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Editor Controls - only show to authenticated users */}
      {canEdit && (
        <StorefrontControls
          isEditing={isEditing}
          hasUnsavedChanges={hasUnsavedChanges}
          isPublishing={isPublishing}
          publishSlug={publishSlug}
          onToggleEditing={() => setIsEditing(!isEditing)}
          onSave={handleSave}
          onPublish={handlePublish}
          onGenerateSlug={generateSlug}
          onPublishSlugChange={setPublishSlug}
          primaryColor={content.colors.primary}
        />
      )}

      <div className="flex min-h-screen">
        {/* Editor Panel - only show to authenticated users when editing */}
        {canEdit && isEditing && (
          <motion.div
            initial={{ x: -320 }}
            animate={{ x: 0 }}
            exit={{ x: -320 }}
            className="w-80 bg-white border-r border-gray-200 shadow-lg"
          >
            <StorefrontEditor
              content={content}
              onChange={handleContentChange}
            />
          </motion.div>
        )}

        {/* Preview/Main Content */}
        <div className={`flex-1 ${canEdit && isEditing ? "ml-0" : ""}`}>
          <StorefrontPreview
            store={currentStore}
            content={content}
            isEditing={isEditing}
            isPublished={isPublished}
            onPublish={handlePublish}
            onContentChange={handleContentChange}
          />
        </div>
      </div>
    </div>
  );
}
